import { useOutletContext } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { ConnectStatus, BackfillJob } from '../lib/api';
import { startJob, listJobs, subscribeJobEvents } from '../lib/api';

interface FilterToggleProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}
function FilterToggle({ label, value, onChange, hint }: FilterToggleProps) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span>
        {label}
        {hint && <small> — {hint}</small>}
      </span>
    </label>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatElapsed(startedAt: string | null | undefined): string {
  if (!startedAt) return '—';
  const ms = Date.now() - new Date(startedAt).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function JobProgressBanner({ job }: { job: BackfillJob }) {
  // Re-render every second so elapsed time keeps ticking for running jobs.
  const [, forceTick] = useState(0);
  useEffect(() => {
    if (job.status !== 'running' && job.status !== 'queued') return;
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [job.status]);

  const isLive = job.status === 'running' || job.status === 'queued';
  const headline =
    job.status === 'running'
      ? 'Backfill in progress'
      : job.status === 'queued'
      ? 'Backfill queued'
      : job.status === 'succeeded'
      ? 'Backfill complete'
      : job.status === 'failed'
      ? 'Backfill failed'
      : 'Backfill cancelled';

  return (
    <div className={`progress-banner status-${job.status}`}>
      <div className="progress-banner-head">
        <span className={`status-pill ${job.status}`}>{job.status}</span>
        <h2>{headline}</h2>
        <span className="progress-banner-time mono">
          {isLive ? `Elapsed ${formatElapsed(job.startedAt ?? job.createdAt)}` : `Finished ${formatDate(job.finishedAt)}`}
        </span>
      </div>

      <div className="progress-stats">
        <div className="progress-stat">
          <span className="progress-stat-label">Processed</span>
          <span className="progress-stat-value num">{job.processed.toLocaleString()}</span>
        </div>
        <div className="progress-stat">
          <span className="progress-stat-label">Imported</span>
          <span className="progress-stat-value num">{job.imported.toLocaleString()}</span>
        </div>
        <div className="progress-stat">
          <span className="progress-stat-label">Skipped</span>
          <span className="progress-stat-value num">{job.skipped.toLocaleString()}</span>
        </div>
      </div>

      {isLive && <div className="progress-bar" aria-hidden="true" />}

      {job.lastError && job.status === 'failed' && (
        <div className="progress-error mono">{job.lastError}</div>
      )}
    </div>
  );
}

export function BackfillPage() {
  const status = useOutletContext<ConnectStatus | null>();
  const [folder, setFolder] = useState<'all' | 'inbox' | 'sentitems'>('all');
  const [hubId, setHubId] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [filterConfig, setFilterConfig] = useState<Record<string, boolean | number>>({
    dropInternalOnly: false,
    dropNoReplySenders: true,
    dropPlatformSenders: true,
    dropNewsletters: true,
    dropCalendarChrome: true,
    dropBounces: true,
    dropRapidDuplicateWindowSeconds: 60,
  });

  const [jobs, setJobs] = useState<BackfillJob[]>([]);
  const [activeJob, setActiveJob] = useState<BackfillJob | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (status?.hubspot[0] && !hubId) setHubId(status.hubspot[0].hubId);
  }, [status, hubId]);

  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      listJobs().then((r) => {
        if (!cancelled) setJobs(r.jobs);
      });
    refresh();
    const t = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (!activeJob) return;
    const unsub = subscribeJobEvents(activeJob.id, (data) => {
      const evt = data as { type: string; counts?: BackfillJob };
      if (evt.counts) {
        setActiveJob((prev) =>
          prev
            ? {
                ...prev,
                processed: evt.counts?.processed ?? prev.processed,
                imported: evt.counts?.imported ?? prev.imported,
                skipped: evt.counts?.skipped ?? prev.skipped,
              }
            : prev
        );
      }
      if (evt.type === 'complete' || evt.type === 'error') {
        // Refresh and propagate the final status (succeeded/failed)
        // + final counts + finishedAt into the banner.
        listJobs().then((r) => {
          setJobs(r.jobs);
          setActiveJob((prev) => {
            if (!prev) return prev;
            const fresh = r.jobs.find((j) => j.id === prev.id);
            return fresh ?? prev;
          });
        });
      }
    });
    return unsub;
  }, [activeJob]);

  // If the user navigates back to /backfill while a job is running and
  // hasn't manually selected one, surface it automatically.
  useEffect(() => {
    if (activeJob) return;
    const live = jobs.find((j) => j.status === 'running' || j.status === 'queued');
    if (live) setActiveJob(live);
  }, [jobs, activeJob]);

  async function onStart() {
    if (!hubId) return;
    setStarting(true);
    try {
      const r = await startJob({
        hubId,
        folder,
        ...(dateFrom ? { dateFrom: new Date(dateFrom).toISOString() } : {}),
        ...(dateTo ? { dateTo: new Date(dateTo).toISOString() } : {}),
        filterConfig,
      });
      const fresh = await listJobs();
      setJobs(fresh.jobs);
      const job = fresh.jobs.find((j) => j.id === r.jobId);
      if (job) setActiveJob(job);
    } catch (e) {
      alert(`Failed to start: ${(e as Error).message}`);
    } finally {
      setStarting(false);
    }
  }

  if (!status) return null;
  const ready = status.microsoft.connected && status.hubspot.length > 0;
  if (!ready) {
    return (
      <div className="page">
        <span className="eyebrow">Not ready</span>
        <h1>Backfill</h1>
        <p className="page-lead">
          Please connect both Microsoft and HubSpot first.
        </p>
      </div>
    );
  }

  return (
    <div className="page backfill">
      <span className="eyebrow">Step 02 · Backfill</span>
      <h1>Send the mail.</h1>
      <p className="page-lead">
        Pick the window, tune the filters, drop it in the box. Jobs run on a worker
        process — you can leave this page open or come back later.
      </p>

      {activeJob && <JobProgressBanner job={activeJob} />}

      <div className="card">
        <div className="card-head">
          <span className="badge">⊕</span>
          <h2>Compose a backfill</h2>
        </div>

        <div className="form-row">
          <label>Portal</label>
          <select value={hubId} onChange={(e) => setHubId(e.target.value)}>
            {status.hubspot.map((c) => (
              <option key={c.hubId} value={c.hubId}>
                {c.hubId}
              </option>
            ))}
          </select>
        </div>
        <div className="form-row">
          <label>Folder</label>
          <select value={folder} onChange={(e) => setFolder(e.target.value as typeof folder)}>
            <option value="all">All folders</option>
            <option value="inbox">Inbox only</option>
            <option value="sentitems">Sent items only</option>
          </select>
        </div>
        <div className="form-row">
          <label>From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <label style={{ width: 'auto' }}>To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>

        <h3>Filters</h3>
        <div className="filters">
          <FilterToggle
            label="Drop internal-only threads"
            value={Boolean(filterConfig.dropInternalOnly)}
            onChange={(v) => setFilterConfig({ ...filterConfig, dropInternalOnly: v })}
            hint="every participant at your domain"
          />
          <FilterToggle
            label="Drop no-reply / automation senders"
            value={Boolean(filterConfig.dropNoReplySenders)}
            onChange={(v) => setFilterConfig({ ...filterConfig, dropNoReplySenders: v })}
            hint="noreply@, postmaster@, etc."
          />
          <FilterToggle
            label="Drop platform senders"
            value={Boolean(filterConfig.dropPlatformSenders)}
            onChange={(v) => setFilterConfig({ ...filterConfig, dropPlatformSenders: v })}
            hint="LinkedIn, Shopify, Monday, Stripe, etc."
          />
          <FilterToggle
            label="Drop newsletters"
            value={Boolean(filterConfig.dropNewsletters)}
            onChange={(v) => setFilterConfig({ ...filterConfig, dropNewsletters: v })}
            hint="emails containing 'unsubscribe' link"
          />
          <FilterToggle
            label="Drop calendar invitations"
            value={Boolean(filterConfig.dropCalendarChrome)}
            onChange={(v) => setFilterConfig({ ...filterConfig, dropCalendarChrome: v })}
          />
          <FilterToggle
            label="Drop bounce notifications"
            value={Boolean(filterConfig.dropBounces)}
            onChange={(v) => setFilterConfig({ ...filterConfig, dropBounces: v })}
            hint="postmaster, mailer-daemon, Undeliverable"
          />
        </div>

        <button className="primary big" disabled={starting} onClick={onStart}>
          {starting ? 'Posting…' : 'Start backfill →'}
        </button>
      </div>

      <div className="card">
        <div className="card-head">
          <span className="badge">⊟</span>
          <h2>Recent jobs</h2>
        </div>
        <table className="jobs-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Status</th>
              <th>Folder</th>
              <th className="num">Processed</th>
              <th className="num">Imported</th>
              <th className="num">Skipped</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr
                key={j.id}
                onClick={() => setActiveJob(j)}
                className={activeJob?.id === j.id ? 'active' : ''}
              >
                <td>{formatDate(j.startedAt ?? j.createdAt)}</td>
                <td>
                  <span className={`status-pill ${j.status}`}>{j.status}</span>
                </td>
                <td className="mono" style={{ fontSize: 13 }}>
                  {j.folder}
                </td>
                <td className="num">{j.processed.toLocaleString()}</td>
                <td className="num">{j.imported.toLocaleString()}</td>
                <td className="num">{j.skipped.toLocaleString()}</td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr>
                <td colSpan={6} className="jobs-empty">
                  No jobs yet · run one above
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
